import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import CalendarMonthIcon from "@mui/icons-material/CalendarMonth";
import EmojiEventsIcon from "@mui/icons-material/EmojiEvents";
import GoogleIcon from "@mui/icons-material/Google";
import GroupsIcon from "@mui/icons-material/Groups";
import LeaderboardIcon from "@mui/icons-material/Leaderboard";
import SportsSoccerIcon from "@mui/icons-material/SportsSoccer";
import { Box, Button, CircularProgress, Grid, Typography } from "@mui/material";
import { useGoogleLogin } from "@react-oauth/google";
import React, { useContext, useState } from "react";
import { useNavigate } from "react-router-dom";
import { djRestAuth } from "./api/api";
import { GoogleButton } from "./GoogleButton";
import { AuthTermsNotice } from "./auth/AuthTermsNotice";
import { UserContext } from "./auth/UserProvider";
const features = [
    {
        icon: _jsx(CalendarMonthIcon, { sx: { fontSize: 48 } }),
        title: "Fixtures & Scheduling",
        description: "Generate full season fixtures in seconds. Round-robin, divisions, venues. All handled.",
    },
    {
        icon: _jsx(LeaderboardIcon, { sx: { fontSize: 48 } }),
        title: "Live Standings",
        description: "League tables update automatically as results come in. Points, goal difference, form. All live.",
    },
    {
        icon: _jsx(SportsSoccerIcon, { sx: { fontSize: 48 } }),
        title: "Player Stats",
        description: "Track goals, assists, and appearances across every match. See who's leading the league.",
    },
    {
        icon: _jsx(GroupsIcon, { sx: { fontSize: 48 } }),
        title: "Team Management",
        description: "Manage rosters, transfers, and team preferences. Invite players and delegate to team managers.",
    },
    {
        icon: _jsx(EmojiEventsIcon, { sx: { fontSize: 48 } }),
        title: "Tournaments",
        description: "Run knockout tournaments and intra-squad competitions alongside your league seasons.",
    },
];
const steps = [
    {
        number: "1",
        title: "Create your league",
        description: "Set up your league, add divisions, and configure your season.",
    },
    {
        number: "2",
        title: "Add teams",
        description: "Invite team managers or create teams yourself. They manage their own rosters.",
    },
    {
        number: "3",
        title: "Generate fixtures",
        description: "One click to generate a full season of fixtures. Edit, reschedule, assign venues.",
    },
];
const Landing = () => {
    const { setUser } = useContext(UserContext);
    const [loading, setLoading] = useState(false);
    const navigate = useNavigate();
    const login = useGoogleLogin({
        onSuccess: (tokenResponse) => {
            setLoading(true);
            djRestAuth
                .post("/google/", {
                accessToken: tokenResponse.access_token,
            })
                .then((response) => {
                const { user } = response.data;
                setUser(user);
                setLoading(false);
                navigate("/");
            })
                .catch(() => {
                setLoading(false);
                setUser(null);
            });
        },
        onError: () => { },
    });
    if (loading) {
        return (_jsx(Box, { sx: { display: "flex", justifyContent: "center", mt: 8 }, children: _jsx(CircularProgress, {}) }));
    }
    return (_jsxs(React.Fragment, { children: [_jsxs(Box, { sx: {
                    mx: { xs: -2, sm: -3 },
                    px: { xs: 3, sm: 6 },
                    py: { xs: 8, sm: 12 },
                    bgcolor: "#2a3557",
                    color: "white",
                    textAlign: "center",
                    borderRadius: 2,
                }, children: [_jsx(Typography, { variant: "h2", component: "h1", sx: {
                            fontWeight: 700,
                            fontSize: { xs: "2rem", sm: "2.75rem", md: "3.5rem" },
                            lineHeight: 1.15,
                            mb: 2,
                            overflowWrap: "break-word",
                        }, children: "The home for your league and team." }), _jsx(Typography, { variant: "h6", component: "p", sx: {
                            maxWidth: 640,
                            mx: "auto",
                            mb: 4,
                            fontWeight: 400,
                            fontSize: { xs: "1rem", sm: "1.25rem" },
                            opacity: 0.9,
                        }, children: "Fixtures, standings, stats, and rosters. One place for the league office and every team manager." }), _jsxs(Box, { sx: {
                            display: "flex",
                            flexDirection: { xs: "column", sm: "row" },
                            gap: 2,
                            justifyContent: "center",
                            alignItems: "center",
                        }, children: [_jsx(Button, { variant: "contained", color: "secondary", size: "large", startIcon: _jsx(GoogleIcon, {}), onClick: () => login(), sx: { fontWeight: 700, px: 4, py: 1.5, fontSize: "1rem" }, children: "Get Started" }), _jsx(Button, { variant: "outlined", size: "large", href: "#how-it-works", sx: {
                                    color: "white",
                                    borderColor: "rgba(255,255,255,0.5)",
                                    "&:hover": {
                                        borderColor: "white",
                                        bgcolor: "rgba(255,255,255,0.1)",
                                    },
                                }, children: "See how it works" })] })] }), _jsxs(Box, { sx: { py: { xs: 6, sm: 10 }, textAlign: "center" }, children: [_jsx(Typography, { variant: "h4", component: "h2", sx: { fontWeight: 700, mb: 1 }, children: "Everything your league needs" }), _jsx(Typography, { variant: "body1", color: "text.secondary", sx: { mb: 6, maxWidth: 500, mx: "auto" }, children: "Stop juggling WhatsApp groups, spreadsheets, and scribbled notes. Log.Soccer brings it all together." }), _jsx(Grid, { container: true, spacing: 4, justifyContent: "center", children: features.map((feature) => (_jsx(Grid, { item: true, xs: 12, sm: 6, md: 4, children: _jsxs(Box, { sx: { px: 2 }, children: [_jsx(Box, { sx: { color: "primary.main", mb: 1.5 }, children: feature.icon }), _jsx(Typography, { variant: "h6", sx: { fontWeight: 600, mb: 1 }, children: feature.title }), _jsx(Typography, { variant: "body2", color: "text.secondary", children: feature.description })] }) }, feature.title))) })] }), _jsxs(Box, { id: "how-it-works", sx: {
                    mx: { xs: -2, sm: -3 },
                    px: { xs: 3, sm: 6 },
                    py: { xs: 6, sm: 10 },
                    bgcolor: "grey.100",
                    textAlign: "center",
                }, children: [_jsx(Typography, { variant: "h4", component: "h2", sx: { fontWeight: 700, mb: 6 }, children: "Up and running in minutes" }), _jsx(Grid, { container: true, spacing: 4, justifyContent: "center", sx: { maxWidth: 900, mx: "auto" }, children: steps.map((step) => (_jsxs(Grid, { item: true, xs: 12, sm: 4, children: [_jsx(Box, { sx: {
                                        width: 56,
                                        height: 56,
                                        borderRadius: "50%",
                                        bgcolor: "secondary.main",
                                        color: "primary.main",
                                        display: "flex",
                                        alignItems: "center",
                                        justifyContent: "center",
                                        mx: "auto",
                                        mb: 2,
                                        fontWeight: 700,
                                        fontSize: "1.5rem",
                                    }, children: step.number }), _jsx(Typography, { variant: "h6", sx: { fontWeight: 600, mb: 1 }, children: step.title }), _jsx(Typography, { variant: "body2", color: "text.secondary", children: step.description })] }, step.number))) })] }), _jsxs(Box, { sx: {
                    py: { xs: 6, sm: 10 },
                    textAlign: "center",
                }, children: [_jsx(Typography, { variant: "h4", component: "h2", sx: { fontWeight: 700, mb: 2 }, children: "Ready to get started?" }), _jsx(Typography, { variant: "body1", color: "text.secondary", sx: { mb: 4, maxWidth: 450, mx: "auto" }, children: "Set up your first season in under 5 minutes." }), _jsxs(Box, { sx: {
                            display: "flex",
                            flexDirection: "column",
                            alignItems: "center",
                        }, children: [_jsx(GoogleButton, { onClick: () => login(), text: "Sign Up with Google" }), _jsx(AuthTermsNotice, {})] })] })] }));
};
export default Landing;
